import { Form, Formik } from 'formik';
import {
  checkUsername,
  getDepartments,
  getProfileData,
  updateUser,
} from 'lib/api';
import { HeaderWrapper } from 'lib/components/styles/layout';
import { Heading } from 'lib/components/styles/typography';
import { successToast } from 'lib/components/toasts/success';
import { PackageName } from 'lib/const/PackageAdditionalInfo';
import { LANGAUGES, useAuth, UserLanguage } from 'lib/context';
import { useToastError } from 'lib/hooks';
import {
  checkIfFeatureIsEnabled,
  isInternalCustomer,
  productFeature,
} from 'lib/utils/productFeature';
import * as React from 'react';
import { useCallback, useState } from 'react';
import { IoMdSave } from 'react-icons/io';
import { Button } from 'react-covideo-common';
import styled from 'styled-components/macro';
import * as Yup from 'yup';
import {
  FlatLeadNotificationPreferences,
  LeadNotificationPreferences,
  UserForm,
  UserValues,
  validateUser,
} from '../components';
import { AddOnsForm } from '../components/AddOnsForm';
import { IntegrationsForm } from '../components/IntegrationsForm';
import { PasswordForm } from '../components/PasswordForm';
import { PreferencesForm } from '../components/PreferencesForm';
import { SignatureForm } from '../components/SignatureForm';
import { WorkingHours } from '../components/WorkingHours';
import { validateInputForCsvFormulaInjection } from 'lib/utils/functions';
import { useHistory } from 'react-router-dom';
import selectors from '../../../../../../cypress/selectors';
import { useUsernameExists } from 'lib/api/users/getUsernameExists';
import { CHANGE_ORIGIN } from 'lib/const/AccountChange';
import { errorToast } from 'lib/components/toasts/error';
import { INotificationPreferences } from '../components/Notifications/common/types';
import RouteLeavingGuard from 'app/pages/video/videoDetails/main/RouteLeavingGuard';
import { AccessRole, ReactionPreferenceOptionsForUser } from 'lib/const';
import { useCDK } from 'lib/hooks/useCdk';
import { useTranslation } from 'react-i18next';
import { TabGroup, TabGroupItem } from 'lib/components/tabs/TabGroup';

export interface IWorkingHours {
  sunday: IWorkingDay | null;
  monday: IWorkingDay | null;
  tuesday: IWorkingDay | null;
  wednesday: IWorkingDay | null;
  thursday: IWorkingDay | null;
  friday: IWorkingDay | null;
  saturday: IWorkingDay | null;
}

export interface IWorkingDay {
  from: IWorkingDayOptions;
  to: IWorkingDayOptions;
}

interface IWorkingDayOptions {
  hours: string;
  meridiem: string;
}

export type ReceivedData = {
  firstName: string;
  lastName: string;
  username: string;
  email: string;
  email2?: string;
  signupDate: string;
  phone1: string;
  phone2?: string;
  cellPhone?: string;
  title?: string;
  autoTranscribe: string;
  transcriptionDefaultEnabled: string;
  eleadDMSEmployeeID: string;
  Dept?: string;
  website?: string;
  timezone?: string;
  playButtonPosition: string;
  signature?: string;
  nmls?: string;
  googleCalendarId?: string;
  calendarWebsiteUrl?: string;
  calendlyCalendarUrl?: string;
  calendlyAccessToken?: string;
  outlookRefreshToken?: string;
  platform?: number;
  reactionsPreference?: any;
  workingHours: IWorkingHours;
  resellerId?: number;
  videoShareLinkText?: string;
  notificationPreferences?: INotificationPreferences;
  leadNotificationPreferences?: Record<
    string,
    Record<string, Array<{ name: string; value: boolean }>>
  >;
  language: UserLanguage;
};

type ProfileProps = {
  activeTab: number;
  setActiveTab: (tab: number) => void;
  data: ReceivedData;
  setProfileUser: Function;
};

const INIT_WORKING_HOURS: IWorkingHours = {
  sunday: null,
  monday: null,
  tuesday: null,
  wednesday: null,
  thursday: null,
  friday: null,
  saturday: null,
};

let fields = {
  signupDate: '',
  firstName: '',
  lastName: '',
  username: '',
  newPassword: '',
  confirmPassword: '',
  email: '',
  email2: '',
  eleadEmployeeId: '',
  phone1: '',
  phone2: '',
  cellPhone: '',
  userTitle: '',
  website: '',
  timezone: '',
  playButtonPosition: '',
  captions: '',
  captionVisibility: '',
  signature: '',
  nmls: '',
  reactionsPreference: '',
  workingHours: INIT_WORKING_HOURS,
};

const getWorkingHours = (data: ReceivedData) => {
  if (data.workingHours === null) {
    return INIT_WORKING_HOURS;
  }

  const parsedData: IWorkingHours = JSON.parse(
    data.workingHours as unknown as string
  );

  if (!Object.keys(parsedData).length) {
    return INIT_WORKING_HOURS;
  }

  return parsedData;
};
const defaultVideoShareLinkText = (firstName: string, lastName: string) => {
  return `Click here to view your video from ${firstName} ${lastName}`;
};
const loadFields = (data: ReceivedData) => {
  return {
    firstName: data.firstName,
    lastName: data.lastName || '',
    username: data.username,
    email: data.email || '',
    email2: data.email2 || '',
    phone1: data.phone1 || '',
    phone2: data.phone2 || '',
    userTitle: data.title || '',
    cellPhone: data.cellPhone || '',
    captions: data.autoTranscribe || '0',
    captionVisibility: data.transcriptionDefaultEnabled || '',
    newPassword: '',
    confirmPassword: '',
    signupDate: data.signupDate,
    eleadEmployeeId: data.eleadDMSEmployeeID || '',
    department: data.Dept?.toString() || null,
    website: data.website || '',
    timezone: data.timezone || '',
    playButtonPosition: data.playButtonPosition,
    signature: data.signature || '',
    platform: data.platform || 0,
    googleCalendarId: data.googleCalendarId || '',
    calendarWebsiteUrl: data.calendarWebsiteUrl || '',
    calendlyCalendarUrl: data.calendlyCalendarUrl || '',
    calendlyAccessToken: data.calendlyAccessToken || '',
    outlookRefreshToken: data.outlookRefreshToken || '',
    nmls: data.nmls || '',
    reactionsPreference:
      data.reactionsPreference ||
      ReactionPreferenceOptionsForUser.TURN_OFF_DEFAULT,
    videoShareLinkText:
      data.videoShareLinkText?.trim() ||
      defaultVideoShareLinkText(data.firstName, data.lastName || ''),
    workingHours: getWorkingHours(data),
    resellerId: data.resellerId || 0,
    language: data.language || LANGAUGES.EN,
    notificationPreferences:
      data.notificationPreferences ||
      ({
        sound: '',
      } as INotificationPreferences),
  };
};

const flattenLeadNotificationPreferences = (
  raw?: Record<string, Record<string, Array<{ name: string; value: boolean }>>>
): Record<string, boolean> => {
  const flat: Record<string, boolean> = {};

  if (!raw) {
    return flat;
  }

  for (const [type, keys] of Object.entries(raw || {})) {
    for (const [key, entries] of Object.entries(keys)) {
      for (const entry of entries) {
        flat[`leadNotificationPreferences.${type}.${key}.${entry.name}`] =
          entry.value;
      }
    }
  }

  return flat;
};

let signature_data = '';

const setSignature = (text: string) => {
  signature_data = text;
};

const TabsContainer = styled.div`
  .tab-container {
    max-width: 100%;
    width: 100%;
  }
  .tab-navigation {
    padding: 0;
    justify-content: flex-start;
  }
  .tab-content {
    padding: 24px 0 0;
    width: 100%;
  }
`;
const EDIT_PATH = '/profile/Profile_EditUserInfo';
export const EditUserProfile = (props: ProfileProps) => {
  const { t, i18n } = useTranslation();
  const formikRef = React.useRef<any>(null);
  const { data, setProfileUser } = props;
  const { userData, invalidateUser } = useAuth();
  const { isCdkUser } = useCDK();
  const { customerId, userId, customer } = userData;
  const showLeadsNotifPref =
    !!userData.customer?.leadsIntegrationActive &&
    !!userData.customer?.leadsNotificationsAccess;

  fields = {
    ...fields,
    ...loadFields(data),
  };
  const history = useHistory();

  const isAutomotiveSales = userData.isAutomotiveSalesRole;
  const showWorkingHours = isInternalCustomer(userData);
  const [isVideoReplyEnabled, setIsVideoReplyEnabled] = useState(true);
  const [isUsernameValid, setUsernameValid] = React.useState(true);

  const fieldsSchema = Yup.object({
    phone1: Yup.string()
      .nullable()
      .transform((v, o) => (o === '' ? undefined : v))
      .min(
        7,
        t('Please enter a phone number equal or longer than 7 characters.')
      )
      .test('isNotSuspicious', 'phone1 is not valid', function (value = '') {
        if (!value) {
          return true;
        }

        return validateInputForCsvFormulaInjection(value);
      }),
    phone2: Yup.string()
      .nullable()
      .transform((v, o) => (o === '' ? undefined : v))
      .min(
        7,
        t('Please enter a phone number equal or longer than 7 characters.')
      )
      .test('isNotSuspicious', 'phone2 is not valid', function (value = '') {
        if (!value) {
          return true;
        }

        return validateInputForCsvFormulaInjection(value);
      }),
    cellPhone: Yup.string()
      .nullable()
      .transform((v, o) => (o === '' ? undefined : v))
      .min(
        7,
        t('Please enter a cell phone number equal or longer than 7 characters.')
      )
      .test(
        'isNotSuspicious',
        'cell phone is not valid',
        function (value = '') {
          if (!value) {
            return true;
          }

          return validateInputForCsvFormulaInjection(value);
        }
      ),
  });

  const packageId = userData?.user?.packageDetails?.id || PackageName.LEGACY;
  const isTrial = userData?.trialAccount;
  const { data: usernameExists } = useUsernameExists({
    search: fields.username,
  });

  // added to show error message for SUS-517
  const { showError } = useToastError();

  const prepareLeadNotificationPreferences = (
    original: LeadNotificationPreferences,
    overrides: FlatLeadNotificationPreferences
  ): FlatLeadNotificationPreferences => {
    const result: FlatLeadNotificationPreferences = {};

    for (const [filterType, groupMap] of Object.entries(original)) {
      result[filterType] = {};

      for (const [groupKey, entries] of Object.entries(groupMap)) {
        result[filterType][groupKey] = {};

        for (const entry of entries) {
          const overrideValue =
            overrides?.[filterType]?.[groupKey]?.[entry.name];

          result[filterType][groupKey][entry.name] =
            typeof overrideValue === 'boolean' ? overrideValue : entry.value;
        }
      }
    }

    return result;
  };

  const preparePatchData = (values: UserValues) => {
    values.firstName = values.firstName.trim();
    values.lastName = values.lastName.trim();
    values.signature = signature_data;
    const videoShareLinkText =
      !!values.videoShareLinkText?.trim() &&
      defaultVideoShareLinkText(values.firstName, values.lastName || '') !==
        values.videoShareLinkText
        ? values.videoShareLinkText?.trim()
        : '';

    return {
      signupDate: values.signupDate,
      firstName: values.firstName,
      lastName: values.lastName,
      username: values.username.trim(),
      newPassword: values.newPassword,
      confirmPassword: values.confirmPassword,
      email: values.email || '',
      email2: values.email2 || null,
      eleadDMSEmployeeID: values.eleadEmployeeId || '',
      phone1: values.phone1 || '',
      phone2: values.phone2 || '',
      cellPhone: values.cellPhone || '',
      title: values.userTitle || '',
      departmentIds: values.department ? [values.department] : [],
      website: values.website || '',
      timezone: values.timezone,
      playButtonPosition: values.playButtonPosition,
      autoTranscribe: values.captions,
      transcriptionDefaultEnabled: values.captionVisibility,
      signature: values.signature,
      platform: values.platform || '0',
      googleCalendarId: values.googleCalendarId || '',
      calendarWebsiteUrl: values.calendarWebsiteUrl || '',
      calendlyCalendarUrl: values.calendlyCalendarUrl || '',
      calendlyAccessToken: values.calendlyAccessToken || '',
      outlookRefreshToken: values.outlookRefreshToken || '',
      nmls: values.nmls || '',
      active: true,
      reactionsPreference: values.reactionsPreference,
      workingHours: values.workingHours,
      notificationPreferences: values.notificationPreferences,
      videoShareLinkText,
      leadNotificationPreferences: prepareLeadNotificationPreferences(
        data.leadNotificationPreferences || {},
        values.leadNotificationPreferences as FlatLeadNotificationPreferences
      ),
      language: values.language,
    };
  };

  const submitData = async (values: UserValues) => {
    try {
      const data = preparePatchData(values);
      const userExist = await checkUsername(data.username, userData.id);
      if (userExist.userExist) {
        errorToast({ title: t('Username is already taken.') });
        return;
      }
      await updateUser(userId, data, true, CHANGE_ORIGIN.myProfile);
      // UPDATE USER
      await invalidateUser();
      // Get data after form is submitted so that latest details will be shown to user
      let profileUser = await getProfileData(userId);
      profileUser = {
        ...profileUser,
        platform: data.platform,
        outlookRefreshToken: data.outlookRefreshToken,
      };
      setProfileUser(profileUser);
      successToast({ title: t('Your personal information has been saved.') });
    } catch (error) {
      // SUS-517 show error message from catch else show common error message, also imported toast notifications.
      showError(
        error ||
          t(
            'An error occurred saving your information, please try again later.'
          )
      );
    }
  };

  const [deptLoading, setDeptLoading] = useState(true);
  const [departmentsOptions, setDepartmentsOptions] = React.useState<
    | {
        value: string;
        label: string;
      }[]
    | []
  >([]);

  const usernameSearch =
    !!usernameExists?.userExist && fields.username !== data.username;

  // TODO: move to react-query
  const getDept = async () => {
    try {
      const dept = await getDepartments(customerId, {});
      const transformedDepartments = dept?.data?.map(dept => ({
        value: dept.DepartmentID.toString(),
        label: dept.Name,
      }));
      setDepartmentsOptions(transformedDepartments || []);
      setDeptLoading(false);
    } catch (error) {
      setDeptLoading(false);
    }
  };

  React.useEffect(() => {
    if (userData.preventUserEditAccess) {
      history.push('/home');
    }
    const userRolesAccess = checkIfFeatureIsEnabled(
      userData,
      productFeature.USER_ROLES_AND_MANAGEMENT
    );

    if (userRolesAccess) {
      getDept();
    }
  }, []);

  React.useEffect(() => {
    setIsVideoReplyEnabled(
      checkIfFeatureIsEnabled(userData, productFeature.VIDEO_REPLY)
    );
  }, [userData]);

  const standardUserWithTranscriptionAccess =
    userData.access === AccessRole.USER && userData.transcriptionAccess;

  const hasAddonsAccess =
    (userData.access !== AccessRole.USER ||
      standardUserWithTranscriptionAccess) &&
    (packageId === PackageName.LEGACY ||
      packageId === PackageName.TEAMS ||
      packageId === PackageName.ENTERPRISE) &&
    !isTrial;

  const validate = useCallback(
    (values: UserValues) => validateUser(values, usernameSearch, t),
    // deps: include whatever can change
    [usernameSearch, t, i18n.resolvedLanguage]
  );

  const showAccountTab = !isCdkUser;
  const showAddOnsTab = hasAddonsAccess && !isCdkUser;
  const showIntegrationsTab = isAutomotiveSales && !isCdkUser;
  const showReactionsPref =
    customer?.reactionsPreference &&
    customer?.reactionsPreference.toString() !== '0' &&
    isVideoReplyEnabled &&
    !isCdkUser;

  const tabGroupItems: TabGroupItem[] = [
    {
      title: t('User info'),
      value: 'user_info',
      component: (
        <UserForm
          departmentsOptions={departmentsOptions}
          deptLoading={deptLoading}
          userId={userId}
          isUsernameValid={isUsernameValid}
          setUsernameValid={setUsernameValid}
        />
      ),
    },
    ...(showAccountTab
      ? [{ title: t('Account'), value: 'account', component: <PasswordForm /> }]
      : []),
    {
      title: t('Signature'),
      value: 'signature',
      component: <SignatureForm setSignatureData={setSignature} />,
    },
    {
      title: t('Preferences'),
      value: 'preferences',
      component: (
        <PreferencesForm
          showReactionsPreference={showReactionsPref}
          packageId={packageId}
          leadNotificationPreferences={data.leadNotificationPreferences}
          showLeadsNotifPref={showLeadsNotifPref}
        />
      ),
    },
    ...(showAddOnsTab
      ? [{ title: t('Add-ons'), value: 'add_ons', component: <AddOnsForm /> }]
      : []),
    ...(showWorkingHours
      ? [
          {
            title: t('Working hours'),
            value: 'working_hours',
            component: <WorkingHours />,
          },
        ]
      : []),
    ...(showIntegrationsTab
      ? [
          {
            title: t('Integrations'),
            value: 'integrations',
            component: <IntegrationsForm />,
          },
        ]
      : []),
  ];

  React.useEffect(() => {
    formikRef.current?.validateForm();
  }, [i18n.resolvedLanguage]);

  return (
    <>
      <Formik
        innerRef={formikRef}
        initialValues={{
          ...fields,
          ...loadFields(data),
          ...flattenLeadNotificationPreferences(
            data?.leadNotificationPreferences
          ),
        }}
        validate={validate}
        validationSchema={fieldsSchema}
        onSubmit={submitData}
        enableReinitialize
      >
        {({ isSubmitting, isValid, values, dirty }) => {
          return (
            <Form autoComplete='new-password'>
              <HeaderWrapper>
                <Heading>{t('Edit My Profile')}</Heading>
                <Button
                  text={isSubmitting ? t('Saving') : t('Save changes')}
                  type='submit'
                  disabled={
                    isSubmitting || !isValid || !dirty || !isUsernameValid
                  }
                  variant='primary'
                  icon={<IoMdSave />}
                  data-cy={selectors.editUserProfile.saveButton}
                />
              </HeaderWrapper>
              <TabsContainer>
                <TabGroup items={tabGroupItems} />
              </TabsContainer>
              <RouteLeavingGuard
                when={true}
                navigate={path => {
                  history.push(path);
                }}
                onConfirm={async () => {
                  await submitData(values);
                }}
                onDiscard={() => {}}
                shouldBlockNavigation={next => {
                  // Allow same-page moves (query/hash changes) on the edit route
                  if (next.pathname.startsWith(EDIT_PATH)) return false;

                  // Block only when leaving the edit page AND the form is dirty
                  return !!dirty;
                }}
              />
            </Form>
          );
        }}
      </Formik>
    </>
  );
};
